import React, { useState, useEffect } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import SearchPinIcon from '../../../../../public/static/images/new_ad/icons/icon-select-location.svg';
import MetroIcon from '../../../../../public/static/images/new_ad/icons/icon-metro.svg';
import { FORM_VALUES } from '../../../../../utils/helpers/constants';
import Icon from '../../../../Icon';
import { useLandmarks } from '../../../../../hooks/useLandmarks';
import { InitCoordinatesType } from '../types';
import {
  chipsStyles,
  LandmarksContainer,
  MapContainer,
  MapOverlay,
  SelectMapButton,
  SelectMapButtonText
} from './styled';
import { FormErrorField } from '../FormErrorField';
import { Chips } from '../../../../ui/Chips';
import { ChipsTheme } from '../../../../ui/Chips/types';
import { Map } from '../../../../ui/MapModal/Map';
import { useGoogleMaps } from '../../../../../providers/GoogleMapsProvider';

interface SelectLocationMapProps {
  initialLocation: InitCoordinatesType;
  shouldRenderMap: boolean;
  setShouldRenderMap: React.Dispatch<React.SetStateAction<boolean>>;
}

export const SelectLocationMap: React.FC<SelectLocationMapProps> = ({
  initialLocation = { lat: 40.409264, lng: 49.867092 },
  shouldRenderMap,
  setShouldRenderMap
}) => {
  const { t } = useTranslation();
  const { change } = useForm();
  const { values } = useFormState();
  const {
    setIsInitializedMap,
    setMapData,
    isOpenModal,
    toggleModal,
    setMapModalData
  } = useGoogleMaps();
  const [pinSelected, setPinSelected] = useState(false);
  const [coordinates, setCoordinates] = useState(initialLocation);

  const { handleLocationDelete, getLandmarks, locations } =
    useLandmarks(coordinates);

  useEffect(() => {
    change(
      FORM_VALUES.locationIds,
      locations?.map(location => location.id)
    );
  }, [locations]);

  const onToggleModal = () => {
    setIsInitializedMap(true);
    toggleModal();
  };

  const saveCoordinates = () => {
    setShouldRenderMap(true);
    setPinSelected(true);
    change(FORM_VALUES.coordinates, coordinates);
    onToggleModal();
    getLandmarks({
      variables: {
        cityId: values[FORM_VALUES.cityId],
        latitude: coordinates?.lat,
        longitude: coordinates?.lng
      }
    });
  };

  useEffect(() => {
    const hasCoordinatesChanged =
      coordinates?.lat !== initialLocation?.lat ||
      coordinates?.lng !== initialLocation?.lng;

    if (hasCoordinatesChanged && !shouldRenderMap) {
      setCoordinates(initialLocation);
    }
  }, [initialLocation, shouldRenderMap]);

  const handleBack = () => {
    if (values[FORM_VALUES.coordinates]) {
      setCoordinates(values[FORM_VALUES.coordinates]);
    }
  };

  useEffect(() => {
    setMapData({
      isOpen: isOpenModal,
      coordinates,
      pinSelected,
      setCoordinates
    });
  }, [
    coordinates?.lat,
    coordinates?.lng,
    pinSelected,
    setCoordinates,
    isOpenModal
  ]);

  useEffect(() => {
    setMapModalData({
      onClose: handleBack,
      saveCoordinates,
      modalId: 'item-form-map-modal'
    });
  }, []);

  const handleContainerClick =
    isOpenModal || !pinSelected ? null : onToggleModal;

  const mapComponent = (
    <Map
      isOpen={isOpenModal}
      coordinates={coordinates}
      pinSelected={pinSelected}
      setCoordinates={setCoordinates}
    />
  );

  return (
    <div>
      <MapContainer
        tabIndex={0}
        role="button"
        onClick={handleContainerClick}
        onKeyPress={handleContainerClick}
        data-cy="item-form-map"
      >
        {shouldRenderMap && (
          <>
            <MapOverlay $isPinSelected />
            {mapComponent}
          </>
        )}
        {!shouldRenderMap && !isOpenModal && (
          <MapOverlay>
            <SelectMapButton
              type="button"
              onClick={onToggleModal}
              data-cy="new-ad-select-on-map"
            >
              <Icon
                width={20}
                height={20}
                IconComponent={SearchPinIcon}
                indent={false}
              />
              <SelectMapButtonText tag="body_1">
                {t('new_ad.select_on_map')}
              </SelectMapButtonText>
            </SelectMapButton>
          </MapOverlay>
        )}
      </MapContainer>

      <FormErrorField
        field={FORM_VALUES.coordinates}
        cypress="coordinates-field-error"
      />

      {!!locations?.length && (
        <LandmarksContainer data-cy="landmarks-container">
          {locations?.map(location => (
            <Chips
              key={location.id}
              text={location.name || ''}
              styles={chipsStyles}
              chipsTheme={ChipsTheme.White}
              icon={location.locationGroupId === '1' ? MetroIcon : null}
              resetHandler={() => handleLocationDelete(location.id)}
              cypress={`landmarks-${location.id}`}
              isTextPadding={false}
              name={`landmarks-${location.id}`}
              dirty
            />
          ))}
        </LandmarksContainer>
      )}
    </div>
  );
};
